import { appAlert } from '@/lib/appAlert';
import { formatAmountForDisplay, formatGroupedAmount, parseGroupedAmount } from '@/lib/amountFormat';
﻿import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
﻿import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { WizardLayout } from '@/components/ui/WizardLayout';
import { ProfessionalTermsOption } from '@/components/ProfessionalTermsOption';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { useTheme } from '@/contexts/ThemeContext';
import { findProfileByEmail } from '@/lib/agreements';
import { supabase } from '@/lib/supabase';
import { createOffer, computeLineItemsTotals, lineItemTotal, type OfferKind, type OfferLineItem } from '@/lib/offers';
import { getBusinessIdentityFields, listMyBusinesses, type MyBusiness } from '@/lib/businesses';
import { CURRENCIES } from '@/lib/currencies';
import { exportOfferPdf } from '@/lib/offerPdf';
import {
  listOfferTemplates,
  saveOfferTemplate,
  deleteOfferTemplate,
  MAX_OFFER_TEMPLATES,
  type OfferTemplate,
} from '@/lib/offerTemplates';
import { DatePickerField } from '@/components/ui/DatePickerField';
import type { Profile } from '@/lib/database.types';
import { searchProfilesByName, type SearchProfileResult } from '@/lib/profiles';

const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';
const GOLD = '#C9A843';
const COACH_KEY = 'offer_coach_v1';

function getCoachSteps(t: (key: string) => string) {
  return [
    { title: t('offersScreens.coach1_title'), text: t('offersScreens.coach1_text'), icon: 'create-outline' },
    { title: t('offersScreens.coach2_title'), text: t('offersScreens.coach2_text'), icon: 'document-text-outline' },
    { title: t('offersScreens.coach3_title'), text: t('offersScreens.coach3_text'), icon: 'cash-outline' },
    { title: t('offersScreens.coach4_title'), text: t('offersScreens.coach4_text'), icon: 'time-outline' },
    { title: t('offersScreens.coach5_title'), text: t('offersScreens.coach5_text'), icon: 'send-outline' },
  ] as const;
}

type Errors = {
  title?: string;
  cost?: string;
  delivery_days?: string;
  recipient_email?: string;
  recipient_name?: string;
  description?: string;
};

function parseCost(raw: string): number | null | undefined {
  if (!raw.trim()) return null;
  const v = parseGroupedAmount(raw);
  if (v == null || !Number.isFinite(v) || v < 0) return undefined;
  return v;
}

function parseDays(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const v = parseInt(trimmed, 10);
  if (!Number.isFinite(v) || v <= 0) return undefined;
  return v;
}

function isValidEmail(e: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
}

export default function NewOfferScreen() {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const COACH_STEPS = useMemo(() => getCoachSteps(t), [t]);
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [includeTerms, setIncludeTerms] = useState(true);

  const { recipientId, recipientName, templateId, presetTitle, presetCost, serviceRequestId } = useLocalSearchParams<{
    recipientId?: string;
    recipientName?: string;
    templateId?: string;
    /** Προ-συμπλήρωση τίτλου/κόστους όταν έρχεται από μια υπηρεσία επαγγελματία (portfolio "Ζήτησε προσφορά"). */
    presetTitle?: string;
    presetCost?: string;
    /** Όταν η προσφορά απαντά σε ένα request board "Ζητώ..." (requests/[id].tsx). */
    serviceRequestId?: string;
  }>();
  const prefilled = !!recipientId;
  const [fallbackRecipientName, setFallbackRecipientName] = useState<string | null>(null);

  // Ασφάλεια: αν κάποιος caller περάσει recipientId χωρίς recipientName (π.χ.
  // ξεχάστηκε), φέρνουμε εμείς το όνομα από το προφίλ αντί να μπλοκάρουμε
  // σιωπηλά το "Επόμενο" σε ένα υποχρεωτικό πεδίο που δεν φαίνεται καν στην οθόνη.
  useEffect(() => {
    if (!recipientId || recipientName?.trim()) return;
    void supabase.rpc('get_profiles_public', { p_ids: [recipientId] })
      .then(({ data }) => setFallbackRecipientName((data?.[0]?.full_name as string | null)?.trim() || null));
  }, [recipientId, recipientName]);

  const [title, setTitle] = useState(presetTitle ?? '');
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState(presetCost ? formatAmountForDisplay(Number(presetCost)) : '');
  const [currency, setCurrency] = useState('EUR');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [deliveryDays, setDeliveryDays] = useState('');
  const [recipientQuery, setRecipientQuery] = useState(recipientName ?? '');
  const [recipientNameManual, setRecipientNameManual] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<{ id: string; full_name: string | null; contact_email?: string | null } | null>(
    recipientId ? { id: recipientId, full_name: recipientName ?? null } : null
  );
  const [nameSuggestions, setNameSuggestions] = useState<SearchProfileResult[]>([]);
  const [emailSuggestion, setEmailSuggestion] = useState<{ id: string; full_name: string | null } | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // ── Πρότυπα προσφορών ──
  const [templates, setTemplates] = useState<OfferTemplate[]>([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [entryChoice, setEntryChoice] = useState<'ask' | 'chosen'>('ask');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showSaveTemplatePrompt, setShowSaveTemplatePrompt] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showReplacePicker, setShowReplacePicker] = useState(false);

  useEffect(() => {
    void listOfferTemplates().then((list) => {
      setTemplates(list);
      setTemplatesLoaded(true);
      if (templateId) {
        const tpl = list.find((t) => t.id === templateId);
        if (tpl) applyTemplate(tpl);
      }
    }).catch(() => setTemplatesLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyTemplate(tpl: OfferTemplate) {
    setTitle(tpl.title);
    setDescription(tpl.description ?? '');
    setOfferKind(tpl.offer_kind);
    if (tpl.offer_kind === 'goods') {
      setLineItems(tpl.line_items && tpl.line_items.length > 0 ? tpl.line_items : [{ description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'amount' }]);
    } else {
      setCost(tpl.cost != null ? formatAmountForDisplay(tpl.cost) : '');
      setDeliveryDays(tpl.delivery_days != null ? String(tpl.delivery_days) : '');
      setOfferType(tpl.type);
      setStartDate(tpl.start_date ? new Date(tpl.start_date) : null);
      setEndDate(tpl.end_date ? new Date(tpl.end_date) : null);
    }
    setIncludeTerms(tpl.include_professional_terms);
    setShowTemplatePicker(false);
    setEntryChoice('chosen');
    setWizardStep(1);
  }

  async function confirmSaveTemplate() {
    const name = templateName.trim();
    if (!name) return;
    setSavingTemplate(true);
    try {
      const saved = await saveOfferTemplate({
        name,
        title: title.trim(),
        description: description.trim() || null,
        cost: offerKind === 'goods' ? goodsFinalCost : (parseCost(cost) != null ? applyVat(parseCost(cost)!) : null),
        delivery_days: offerKind === 'service' && offerType !== 'fixed_term' ? (parseDays(deliveryDays) ?? null) : null,
        type: offerKind === 'goods' ? 'one_off' : offerType,
        start_date: offerKind === 'service' && offerType === 'fixed_term' && startDate ? startDate.toISOString().slice(0, 10) : null,
        end_date: offerKind === 'service' && offerType === 'fixed_term' && endDate ? endDate.toISOString().slice(0, 10) : null,
        include_professional_terms: includeTerms,
        offer_kind: offerKind,
        line_items: offerKind === 'goods' ? lineItems : null,
      });
      setTemplates((prev) => [...prev, saved]);
      setShowSaveTemplatePrompt(false);
      setTemplateName('');
    } catch (e) {
      if (getErrorMessage(e, '').includes('3 αποθηκευμένα')) {
        setShowReplacePicker(true);
      } else {
        appAlert(t('common.error'), getErrorMessage(e, t('offersScreens.saveTemplateFailed')));
      }
    } finally {
      setSavingTemplate(false);
    }
  }

  async function replaceTemplate(oldId: string) {
    setSavingTemplate(true);
    try {
      await deleteOfferTemplate(oldId);
      setTemplates((prev) => prev.filter((t) => t.id !== oldId));
      setShowReplacePicker(false);
      await confirmSaveTemplate();
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('offersScreens.replaceTemplateFailed')));
    } finally {
      setSavingTemplate(false);
    }
  }

  // ── Tutorial bottom sheet ──
  const [coachStep, setCoachStep] = useState<number | null>(null);

  useEffect(() => {
    void AsyncStorage.getItem(COACH_KEY).then((v) => {
      if (!v) setTimeout(() => setCoachStep(0), 400);
    });
  }, []);

  function nextCoach() {
    const next = (coachStep ?? 0) + 1;
    if (next >= COACH_STEPS.length) {
      setCoachStep(null);
      void AsyncStorage.setItem(COACH_KEY, '1');
    } else {
      setCoachStep(next);
    }
  }

  function skipCoach() {
    setCoachStep(null);
    void AsyncStorage.setItem(COACH_KEY, '1');
  }

  const isEmailMode = recipientQuery.includes('@');

  useEffect(() => {
    setNameSuggestions([]);
    setEmailSuggestion(null);
    if (selectedUser || prefilled) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const trimmed = recipientQuery.trim();
    if (!trimmed) return;

    if (isEmailMode) {
      if (trimmed.length < 5) return;
      searchTimer.current = setTimeout(async () => {
        try {
          const found = await findProfileByEmail(trimmed);
          if (found && found.id !== user?.id) setEmailSuggestion(found);
        } catch { /* αγνόησε */ }
      }, 500);
    } else {
      if (trimmed.length < 2) return;
      searchTimer.current = setTimeout(async () => {
        try {
          const results = await searchProfilesByName(trimmed, user?.id);
          setNameSuggestions(results.slice(0, 5));
        } catch { /* αγνόησε */ }
      }, 350);
    }
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [recipientQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectUser(p: { id: string; full_name: string | null; contact_email?: string | null }) {
    setSelectedUser(p);
    setRecipientQuery(p.full_name?.trim() || p.contact_email || '');
    setNameSuggestions([]);
    setEmailSuggestion(null);
  }

  const [offerType, setOfferType] = useState<'one_off' | 'ongoing' | 'fixed_term'>('one_off');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Αν ο χρήστης είναι συνέταιρος (όχι ιδιοκτήτης) σε μία ή περισσότερες
  // επιχειρήσεις, επιλέγει με ποια ταυτότητα στέλνει: προσωπικά ("Εγώ" —
  // ταυτίζεται με τη δική του επιχείρηση αν είναι ο ιδιοκτήτης της) ή ως
  // κάποια από τις επιχειρήσεις όπου είναι απλό μέλος (ένα πλακίδιο ανά
  // επιχείρηση — μπορεί να είναι συνέταιρος σε παραπάνω από μία).
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [memberBusinesses, setMemberBusinesses] = useState<MyBusiness[]>([]);
  useEffect(() => {
    void listMyBusinesses()
      .then((all) => setMemberBusinesses(all.filter((b) => b.my_role === 'member')))
      .catch(() => setMemberBusinesses([]));
  }, [user?.id]);

  const [offerKind, setOfferKind] = useState<OfferKind>('service');
  const [lineItems, setLineItems] = useState<OfferLineItem[]>([{ description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'amount' }]);
  const lineItemsTotals = useMemo(() => computeLineItemsTotals(lineItems), [lineItems]);
  const hasAnyDiscount = lineItems.some((it) => (Number(it.discount) || 0) > 0);

  /**
   * "Χωρίς ΦΠΑ": η τιμή που εισάγεται είναι καθαρή (χωρίς ΦΠΑ) — δίνεται πεδίο
   * για χειροκίνητο ΦΠΑ % και το τελικό ποσό υπολογίζεται με προσθήκη του ΦΠΑ.
   * "Με ΦΠΑ": η τιμή θεωρείται ήδη τελική (με ΦΠΑ) — δεν προστίθεται τίποτα επιπλέον.
   * Ίδιο toggle και για τα δύο είδη προσφοράς (υπηρεσία/εμπόρευμα).
   */
  const [vatMode, setVatMode] = useState<'without' | 'with'>('without');
  const [vatRate, setVatRate] = useState('24');
  const vatRateNum = Number(vatRate.replace(',', '.')) || 0;
  function applyVat(net: number): number {
    if (vatMode !== 'without') return net;
    return Math.round((net + net * (vatRateNum / 100)) * 100) / 100;
  }

  const vatAmount = vatMode === 'without' ? Math.round(lineItemsTotals.finalTotal * (vatRateNum / 100) * 100) / 100 : 0;
  const totalWithVat = applyVat(lineItemsTotals.finalTotal);
  const goodsFinalCost = totalWithVat;

  const serviceCostNum = parseCost(cost) ?? 0;
  const serviceVatAmount = vatMode === 'without' ? Math.round(serviceCostNum * (vatRateNum / 100) * 100) / 100 : 0;
  const serviceTotalWithVat = applyVat(serviceCostNum);

  function addLineItem() {
    setLineItems((prev) => [...prev, { description: '', quantity: 1, unitPrice: 0, discount: 0, discountType: 'amount' }]);
  }
  function removeLineItem(index: number) {
    setLineItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }
  function updateLineItem(index: number, patch: Partial<OfferLineItem>) {
    setLineItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }

  const [createdOfferId, setCreatedOfferId] = useState<string | null>(null);
  const [createdTitle, setCreatedTitle] = useState('');
  const [createdInviteCode, setCreatedInviteCode] = useState<string | null>(null);

  /** Τελικό ονοματεπώνυμο παραλήπτη — από εγγεγραμμένο χρήστη, το κύριο πεδίο
   * αναζήτησης (όταν περιέχει όνομα), ή το ξεχωριστό πεδίο ονόματος (όταν το
   * κύριο πεδίο είναι email). Πάντα υποχρεωτικό. */
  const resolvedRecipientName = prefilled
    ? (recipientName?.trim() || fallbackRecipientName || null)
    : selectedUser
    ? (selectedUser.full_name?.trim() || null)
    : isEmailMode
    ? (recipientNameManual.trim() || null)
    : (recipientQuery.trim() || null);

  function validateStep1(): boolean {
    const next: Errors = {};
    if (!title.trim()) next.title = t('offersScreens.errorTitleRequired');
    if (!resolvedRecipientName) next.recipient_name = t('offersScreens.errorRecipientNameRequired');
    if (next.title || next.recipient_name) { setErrors(next); return false; }
    setErrors({});
    return true;
  }

  function validateStep2(): boolean {
    const next: Errors = {};
    if (!description.trim()) next.description = t('offersScreens.errorDescriptionRequired');
    if (offerKind === 'goods') {
      const valid = lineItems.every((it) => it.description.trim().length > 0 && it.quantity > 0 && it.unitPrice >= 0);
      if (!valid) next.cost = t('offersScreens.errorProductsRequired');
      if (next.cost || next.description) { setErrors(next); return false; }
      setErrors({});
      return true;
    }
    const parsedCost = parseCost(cost);
    if (parsedCost === undefined) next.cost = t('offersScreens.errorInvalidAmount');
    if (offerType !== 'fixed_term') {
      const parsedDays = parseDays(deliveryDays);
      if (parsedDays === undefined) next.delivery_days = t('offersScreens.errorInvalidDays');
    } else {
      if (!startDate) next.delivery_days = t('offersScreens.errorStartDateRequired');
      else if (!endDate) next.delivery_days = t('offersScreens.errorEndDateRequired');
      else if (endDate <= startDate) next.delivery_days = t('offersScreens.errorEndAfterStart');
    }
    if (next.cost || next.delivery_days || next.description) { setErrors(next); return false; }
    setErrors({});
    return true;
  }

  async function onSubmit() {
    setFormError(null);
    const next: Errors = {};
    const parsedCostRaw = parseCost(cost);
    const parsedCost = offerKind === 'goods' ? goodsFinalCost : (parsedCostRaw != null ? applyVat(parsedCostRaw) : parsedCostRaw);
    if (offerKind === 'service' && parsedCostRaw === undefined) next.cost = t('offersScreens.errorInvalidAmount');
    const parsedDays = offerKind === 'service' ? parseDays(deliveryDays) : null;
    if (offerKind === 'service' && parsedDays === undefined) next.delivery_days = t('offersScreens.errorInvalidDays');

    let resolvedEmail: string | null = null;
    if (prefilled || selectedUser) {
      resolvedEmail = selectedUser?.contact_email ?? null;
    } else if (isEmailMode) {
      const trimmed = recipientQuery.trim();
      if (trimmed && !isValidEmail(trimmed)) {
        next.recipient_email = t('offersScreens.errorInvalidEmail');
      } else {
        resolvedEmail = trimmed || null;
      }
    }

    if (Object.keys(next).length) { setErrors(next); return; }
    setErrors({});

    setLoading(true);
    try {
      const offer = await createOffer({
        title: title.trim(),
        description: description.trim() || null,
        cost: offerKind === 'goods' ? goodsFinalCost : (parsedCost ?? null),
        currency: offerKind === 'goods' ? 'EUR' : currency,
        delivery_days: offerKind === 'service' && offerType !== 'fixed_term' ? (parsedDays ?? null) : null,
        recipient_email: resolvedEmail,
        recipient_id: (prefilled || selectedUser) ? selectedUser?.id ?? null : null,
        recipient_name: resolvedRecipientName,
        type: offerKind === 'goods' ? 'one_off' : offerType,
        start_date: offerKind === 'service' && offerType === 'fixed_term' && startDate ? startDate.toISOString().slice(0, 10) : null,
        end_date: offerKind === 'service' && offerType === 'fixed_term' && endDate ? endDate.toISOString().slice(0, 10) : null,
        includeProfessionalTerms: includeTerms,
        offerKind,
        lineItems: offerKind === 'goods' ? lineItems : null,
        serviceRequestId: serviceRequestId ?? null,
        senderBusinessId: selectedBusinessId,
      });
      setCreatedOfferId(offer.id);
      setCreatedTitle(title.trim());
      setCreatedInviteCode((offer as { invite_code?: string | null }).invite_code ?? null);
      if (resolvedEmail) setRecipientEmail(resolvedEmail);
      // Το custom email στέλνεται αυτόματα μέσω DB trigger → notify-offer edge function
      setTimeout(() => {
        appAlert(
          t('offersScreens.saveAsTemplateTitle'),
          t('offersScreens.saveAsTemplateMessage'),
          [
            { text: t('common.no'), style: 'cancel' },
            { text: t('common.yes'), onPress: () => setShowSaveTemplatePrompt(true) },
          ],
        );
      }, 500);
    } catch (e) {
      const rawMsg = getErrorMessage(e, t('offersScreens.createOfferFailed'));
      const dupMatch = rawMsg.match(/duplicate_business_offer:(.+)/);
      if (dupMatch) {
        setFormError(t('offersScreens.duplicateBusinessOffer', { name: dupMatch[1].trim() }));
      } else {
        setFormError(rawMsg);
      }
    } finally {
      setLoading(false);
    }
  }

  async function onShare() {
    if (!createdOfferId) return;
    // Ο κωδικός ΔΕΝ μπαίνει στο URL — αν το link σταλεί κατά λάθος σε λάθος
    // άτομο, δεν πρέπει να μπορεί να δει την προσφορά αυτόματα. Μένει μόνο
    // ως ξεχωριστό κείμενο στο μήνυμα (codeLine), που ο παραλήπτης αντιγράφει.
    const url = `https://heirapsia.gr/landing-page/offer-invite.html?offer=${createdOfferId}`;
    const codeLine = createdInviteCode ? t('offers.share_invite_code', { code: createdInviteCode }) : '';

    // Δείχνουμε ποιος στέλνει την προσφορά (επιχείρηση αν στάλθηκε ως τέτοια,
    // αλλιώς το προφίλ) αντί για τη γενική "Μοιράσου τον παρακάτω σύνδεσμο".
    let senderName = (profile?.show_business_name && profile.business_name?.trim()) || profile?.full_name?.trim() || '';
    if (selectedBusinessId) {
      const biz = await getBusinessIdentityFields(selectedBusinessId);
      if (biz?.business_name) senderName = biz.business_name;
    }
    const fromLine = senderName ? t('offers.shareFromLine', { name: senderName }) : t('offers.created_body');

    await Share.share({
      title: createdTitle,
      message: `${fromLine}\n${url}${codeLine}`,
      url,
    });
  }

  const [printingOffer, setPrintingOffer] = useState(false);
  async function onPrintOffer() {
    if (!profile || !resolvedRecipientName) return;
    setPrintingOffer(true);
    try {
      // Αν στάλθηκε "ως η επιχείρηση", το PDF δείχνει τα επιχειρηματικά στοιχεία
      // (λογότυπο/όροι) του δημιουργού της επιχείρησης, όχι του προφίλ μου.
      let printProfile = profile as Profile & { professional_terms?: string | null; business_logo_url?: string | null; business_name?: string | null; show_business_name?: boolean | null };
      if (selectedBusinessId) {
        const bizFields = await getBusinessIdentityFields(selectedBusinessId);
        if (bizFields) printProfile = { ...printProfile, ...bizFields };
      }
      await exportOfferPdf(
        {
          title: createdTitle,
          description: description.trim() || null,
          cost: offerKind === 'goods' ? goodsFinalCost : (parseCost(cost) != null ? applyVat(parseCost(cost)!) : null),
          delivery_days: offerKind === 'service' && offerType !== 'fixed_term' ? (parseDays(deliveryDays) ?? null) : null,
          offer_kind: offerKind,
          line_items: offerKind === 'goods' ? lineItems : null,
          created_at: new Date().toISOString(),
        },
        resolvedRecipientName,
        printProfile,
      );
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('offersScreens.printFailed')));
    } finally {
      setPrintingOffer(false);
    }
  }

  const recipientIsRegistered = prefilled || !!selectedUser;

  if (createdOfferId) {
    return (
      <View style={[styles.flex, styles.successWrap, { paddingTop: insets.top + spacing.xl, paddingBottom: insets.bottom + spacing.lg }]}>
        <View style={styles.successIcon}>
          <Ionicons name="checkmark-circle" size={64} color={colors.success} />
        </View>
        <Text style={styles.successTitle}>{t('offers.created_title')}</Text>

        {recipientIsRegistered ? (
          <View style={styles.deliveredBanner}>
            <Ionicons name="checkmark-done-outline" size={18} color={colors.success} />
            <Text style={styles.deliveredBannerText}>
              {t('offersScreens.successBodyRegistered', { name: resolvedRecipientName ?? '' })}
            </Text>
          </View>
        ) : (
          <>
            <Text style={styles.successText}>
              {t('offersScreens.successBody')}
            </Text>
            {!!resolvedRecipientName && (
              <View style={styles.emailSentBanner}>
                <Ionicons name="person-outline" size={18} color={colors.success} />
                <Text style={styles.emailSentText}>
                  {t('offersScreens.offerAddressedTo', { name: resolvedRecipientName })}
                </Text>
              </View>
            )}
          </>
        )}

        <Button title={t('offers.action_shareLink')} icon="share-outline" onPress={onShare} />
        <Button
          title={t('offersScreens.printOrSendOffer')}
          icon="print-outline"
          variant="secondary"
          onPress={onPrintOffer}
          loading={printingOffer}
        />

        {!!recipientEmail && (
          <View style={styles.emailSentBanner}>
            <Ionicons name="mail-outline" size={18} color={colors.success} />
            <Text style={styles.emailSentText}>
              {t('offersScreens.emailSentTo', { email: recipientEmail.trim() })}
            </Text>
          </View>
        )}

        <Button title={t('common.back')} variant="secondary" onPress={() => router.back()} />

        {showSaveTemplatePrompt && (
          <Modal visible transparent animationType="fade" onRequestClose={() => setShowSaveTemplatePrompt(false)}>
            <View style={styles.modalOverlay}>
              <View style={styles.modalCard}>
                <Text style={styles.modalTitle}>{t('offersScreens.templateNameTitle')}</Text>
                <Input
                  label={t('offersScreens.templateNameLabel')}
                  value={templateName}
                  onChangeText={setTemplateName}
                  placeholder={t('offersScreens.templateNamePlaceholder')}
                />
                <View style={styles.modalActions}>
                  <View style={{ flex: 1 }}>
                    <Button title={t('common.cancel')} variant="secondary" onPress={() => { setShowSaveTemplatePrompt(false); setTemplateName(''); }} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Button title={t('common.save')} onPress={confirmSaveTemplate} loading={savingTemplate} disabled={!templateName.trim()} />
                  </View>
                </View>
              </View>
            </View>
          </Modal>
        )}

        {showReplacePicker && (
          <Modal visible transparent animationType="fade" onRequestClose={() => setShowReplacePicker(false)}>
            <View style={styles.modalOverlay}>
              <View style={styles.modalCard}>
                <Text style={styles.modalTitle}>{t('offersScreens.hasThreeTemplatesTitle')}</Text>
                <Text style={styles.fieldHint}>{t('offersScreens.chooseTemplateToReplace')}</Text>
                {templates.map((tpl) => (
                  <Pressable key={tpl.id} style={styles.templateRow} onPress={() => void replaceTemplate(tpl.id)} disabled={savingTemplate}>
                    <Ionicons name="document-text-outline" size={16} color={colors.primary} />
                    <Text style={styles.templateRowText} numberOfLines={1}>{tpl.name}</Text>
                  </Pressable>
                ))}
                <Button title={t('common.cancel')} variant="secondary" onPress={() => setShowReplacePicker(false)} />
              </View>
            </View>
          </Modal>
        )}
      </View>
    );
  }

  if (templatesLoaded && templates.length > 0 && entryChoice === 'ask') {
    return (
      <View style={[styles.flex, { paddingTop: insets.top + spacing.xl, paddingHorizontal: spacing.lg, gap: spacing.md }]}>
        <Pressable onPress={() => router.back()} hitSlop={10} accessibilityRole="button" style={{ marginBottom: spacing.md }}>
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <Text style={styles.title}>{t('offersScreens.newOfferTitle')}</Text>
        <Text style={styles.subtitle}>{t('offersScreens.entryChoiceSubtitle')}</Text>

        <Pressable style={styles.entryCard} onPress={() => setEntryChoice('chosen')} accessibilityRole="button">
          <Ionicons name="add-circle-outline" size={28} color={colors.primary} />
          <View style={{ flex: 1 }}>
            <Text style={styles.entryCardTitle}>{t('offersScreens.entryNewTitle')}</Text>
            <Text style={styles.entryCardSub}>{t('offersScreens.entryNewSub')}</Text>
          </View>
        </Pressable>

        <Pressable style={styles.entryCard} onPress={() => setShowTemplatePicker(true)} accessibilityRole="button">
          <Ionicons name="copy-outline" size={28} color={colors.primary} />
          <View style={{ flex: 1 }}>
            <Text style={styles.entryCardTitle}>{t('offersScreens.entryTemplateTitle')}</Text>
            <Text style={styles.entryCardSub}>{t('offersScreens.entryTemplateSub', { count: templates.length })}</Text>
          </View>
        </Pressable>

        <Pressable
          style={styles.manageTemplatesLink}
          onPress={() => router.push('/(app)/offers/templates')}
          accessibilityRole="button"
        >
          <Ionicons name="settings-outline" size={15} color={colors.primary} />
          <Text style={styles.manageTemplatesLinkText}>{t('offersScreens.manageTemplates')}</Text>
        </Pressable>

        {showTemplatePicker && (
          <Modal visible transparent animationType="fade" onRequestClose={() => setShowTemplatePicker(false)}>
            <View style={styles.modalOverlay}>
              <View style={styles.modalCard}>
                <Text style={styles.modalTitle}>{t('offersScreens.chooseTemplateTitle')}</Text>
                {templates.map((tpl) => (
                  <Pressable key={tpl.id} style={styles.templateRow} onPress={() => applyTemplate(tpl)}>
                    <Ionicons name="document-text-outline" size={16} color={colors.primary} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.templateRowText} numberOfLines={1}>{tpl.name}</Text>
                      <Text style={styles.templateRowSub} numberOfLines={1}>{tpl.title}</Text>
                    </View>
                  </Pressable>
                ))}
                <Button title={t('common.cancel')} variant="secondary" onPress={() => setShowTemplatePicker(false)} />
              </View>
            </View>
          </Modal>
        )}
      </View>
    );
  }

  const recipientDisplay = prefilled
    ? (recipientName?.trim() || fallbackRecipientName || t('common.selectedUser'))
    : selectedUser ? (selectedUser.full_name?.trim() || recipientQuery.trim())
    : recipientQuery.trim() || '—';

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        contentContainerStyle={[
          styles.content,
          { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.lg },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <WizardLayout
          step={wizardStep}
          totalSteps={3}
          title={wizardStep === 1 ? t('wizard.offer_step1_title') : wizardStep === 2 ? t('wizard.offer_step2_title') : t('wizard.offer_step3_title')}
          subtitle={
            wizardStep === 1
              ? t('wizard.offer_step1_subtitle')
              : wizardStep === 2
              ? t('wizard.offer_step2_subtitle')
              : t('wizard.offer_step3_subtitle')
          }
          onBack={wizardStep > 1 ? () => { setWizardStep((s) => s - 1); setErrors({}); } : undefined}
          onClose={() => router.back()}
        />

        <View style={styles.form}>

          {/* ══ Βήμα 1 ══ */}
          {wizardStep === 1 && (
            <>
              <Input
                label={t('offers.field_title')}
                value={title}
                onChangeText={setTitle}
                error={errors.title}
                placeholder={t('offersScreens.titlePlaceholder')}
              />

              <View style={styles.field}>
                <Text style={styles.fieldLabel}>{t('offers.field_recipient')}</Text>
                {prefilled ? (
                  <View style={styles.selectedChip}>
                    <Ionicons name="person-circle-outline" size={18} color={colors.primary} />
                    <Text style={styles.selectedChipText} numberOfLines={1}>{recipientName?.trim() || fallbackRecipientName || t('common.selectedUser')}</Text>
                  </View>
                ) : (
                  <View style={styles.searchFieldWrap}>
                    <View style={[styles.inputWrap, !!errors.recipient_email && styles.inputError]}>
                      {selectedUser ? (
                        <Ionicons name="person-circle-outline" size={18} color={colors.primary} style={{ marginRight: 4 }} />
                      ) : (
                        <Ionicons name="search" size={15} color={colors.textMuted} style={{ marginRight: 4 }} />
                      )}
                      <TextInput
                        style={styles.input}
                        value={recipientQuery}
                        onChangeText={(v) => { setRecipientQuery(v); if (selectedUser) setSelectedUser(null); }}
                        placeholder={t('offersScreens.recipientPlaceholder')}
                        placeholderTextColor={colors.textMuted}
                        autoCapitalize="none"
                        autoCorrect={false}
                      />
                      {(selectedUser || recipientQuery.length > 0) && (
                        <Pressable hitSlop={8} onPress={() => { setRecipientQuery(''); setSelectedUser(null); setNameSuggestions([]); setEmailSuggestion(null); }}>
                          <Ionicons name="close-circle" size={17} color={colors.textMuted} />
                        </Pressable>
                      )}
                    </View>
                    {(nameSuggestions.length > 0 || emailSuggestion) && (
                      <View style={styles.dropdown}>
                        {nameSuggestions.map((p) => (
                          <Pressable key={p.id} style={styles.dropdownItem} onPress={() => selectUser(p)}>
                            <Ionicons name="person-outline" size={15} color={colors.textMuted} />
                            <View style={styles.dropdownMeta}>
                              <Text style={styles.dropdownName}>{p.full_name?.trim() || '—'}</Text>
                              {p.contact_email ? <Text style={styles.dropdownEmail}>{p.contact_email}</Text> : null}
                            </View>
                          </Pressable>
                        ))}
                        {emailSuggestion && (
                          <Pressable style={styles.dropdownItem} onPress={() => selectUser(emailSuggestion)}>
                            <Ionicons name="person-outline" size={15} color={colors.textMuted} />
                            <View style={styles.dropdownMeta}>
                              <Text style={styles.dropdownName}>{emailSuggestion.full_name?.trim() || recipientQuery.trim()}</Text>
                              <Text style={styles.dropdownSub}>{t('hint.existsInApp')}</Text>
                            </View>
                          </Pressable>
                        )}
                      </View>
                    )}
                  </View>
                )}
                {!!errors.recipient_email && <Text style={styles.errorText}>{errors.recipient_email}</Text>}
                <Text style={styles.fieldHint}>
                  {selectedUser && !prefilled
                    ? t('offersScreens.recipientHintUser')
                    : t('offersScreens.recipientHintSearch')}
                </Text>

                {!prefilled && !selectedUser && isEmailMode && (
                  <View style={{ marginTop: spacing.sm }}>
                    <Input
                      label={t('offersScreens.recipientNameLabel')}
                      value={recipientNameManual}
                      onChangeText={setRecipientNameManual}
                      error={errors.recipient_name}
                      placeholder={t('offersScreens.recipientNamePlaceholder')}
                    />
                  </View>
                )}
                {!prefilled && !selectedUser && !isEmailMode && !!errors.recipient_name && (
                  <Text style={styles.errorText}>{errors.recipient_name}</Text>
                )}
              </View>

              <View style={styles.field}>
                <Text style={styles.fieldLabel}>{t('offersScreens.offerKindLabel')}</Text>
                <View style={styles.typeCards}>
                  <Pressable
                    style={[styles.typeCard, offerKind === 'service' && styles.typeCardSelected]}
                    onPress={() => setOfferKind('service')}
                    accessibilityRole="button"
                  >
                    <Ionicons name="construct-outline" size={20} color={offerKind === 'service' ? colors.primary : colors.textMuted} />
                    <Text style={[styles.typeCardLabel, offerKind === 'service' && styles.typeCardLabelSelected]}>{t('offersScreens.offerKindService')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.typeCard, offerKind === 'goods' && styles.typeCardSelected]}
                    onPress={() => setOfferKind('goods')}
                    accessibilityRole="button"
                  >
                    <Ionicons name="cube-outline" size={20} color={offerKind === 'goods' ? colors.primary : colors.textMuted} />
                    <Text style={[styles.typeCardLabel, offerKind === 'goods' && styles.typeCardLabelSelected]}>{t('offersScreens.offerKindGoods')}</Text>
                  </Pressable>
                </View>
              </View>

              {memberBusinesses.length > 0 && (
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>{t('offersScreens.sendAsLabel')}</Text>
                  <View style={[styles.typeCards, { flexWrap: 'wrap' }]}>
                    <Pressable
                      style={[styles.typeCard, !selectedBusinessId && styles.typeCardSelected]}
                      onPress={() => setSelectedBusinessId(null)}
                      accessibilityRole="button"
                    >
                      <Ionicons name="person-outline" size={20} color={!selectedBusinessId ? colors.primary : colors.textMuted} />
                      <Text style={[styles.typeCardLabel, !selectedBusinessId && styles.typeCardLabelSelected]} numberOfLines={1}>
                        {(profile?.show_business_name && profile.business_name?.trim())
                          || profile?.full_name?.trim()
                          || t('offersScreens.sendAsMe')}
                      </Text>
                    </Pressable>
                    {memberBusinesses.map((biz) => (
                      <Pressable
                        key={biz.business_id}
                        style={[styles.typeCard, selectedBusinessId === biz.business_id && styles.typeCardSelected]}
                        onPress={() => setSelectedBusinessId(biz.business_id)}
                        accessibilityRole="button"
                      >
                        <Ionicons name="briefcase-outline" size={20} color={selectedBusinessId === biz.business_id ? colors.primary : colors.textMuted} />
                        <Text style={[styles.typeCardLabel, selectedBusinessId === biz.business_id && styles.typeCardLabelSelected]} numberOfLines={1}>
                          {biz.business_name}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              )}

              <Button title={t('common.next')} onPress={() => { if (validateStep1()) setWizardStep(2); }} />
            </>
          )}

          {/* ══ Βήμα 2 ══ */}
          {wizardStep === 2 && offerKind === 'service' && (
            <>
              {/* Τύπος προσφοράς */}
              <View style={styles.field}>
                <Text style={styles.fieldLabel}>{t('offers.type_label')}</Text>
                <View style={styles.typeCards}>
                  {([
                    { key: 'one_off' as const, label: t('offers.type_one_off'), icon: 'flash-outline', sub: t('offers.type_one_off_sub') },
                    { key: 'ongoing' as const, label: t('offers.type_ongoing'), icon: 'infinite-outline', sub: t('offers.type_ongoing_sub') },
                    { key: 'fixed_term' as const, label: t('offers.type_fixed_term'), icon: 'calendar-outline', sub: t('offers.type_fixed_term_sub') },
                  ] as const).map((opt) => {
                    const sel = offerType === opt.key;
                    return (
                      <Pressable
                        key={opt.key}
                        onPress={() => setOfferType(opt.key)}
                        style={[styles.typeCard, sel && styles.typeCardSelected]}
                        accessibilityRole="button"
                      >
                        <Ionicons name={opt.icon} size={20} color={sel ? colors.primary : colors.textMuted} />
                        <Text style={[styles.typeCardLabel, sel && styles.typeCardLabelSelected]}>{opt.label}</Text>
                        <Text style={styles.typeCardSub}>{opt.sub}</Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>

              {/* Κόστος */}
              <View style={styles.field}>
                <Text style={styles.fieldLabel}>{t('offers.field_cost')}</Text>
                <View style={[styles.inputWrap, !!errors.cost && styles.inputError]}>
                  <TextInput
                    style={styles.input}
                    value={cost}
                    onChangeText={(v) => setCost(formatGroupedAmount(v))}
                    placeholder={t('offersScreens.costPlaceholder')}
                    placeholderTextColor={colors.textMuted}
                    keyboardType="decimal-pad"
                  />
                  <Text style={styles.inputSuffix}>{CURRENCIES.find((c) => c.code === currency)?.symbol ?? currency}</Text>
                </View>
                {!!errors.cost && <Text style={styles.errorText}>{errors.cost}</Text>}
              </View>

              <View style={styles.field}>
                <Text style={styles.fieldLabel}>{t('offersScreens.currencyLabel')}</Text>
                <Pressable
                  style={styles.currencyTrigger}
                  onPress={() => setShowCurrencyDropdown((v) => !v)}
                  accessibilityRole="button"
                >
                  {(() => { const sel = CURRENCIES.find((c) => c.code === currency)!; return (
                    <>
                      <Text style={styles.currencyTriggerSymbol}>{sel.symbol}</Text>
                      <Text style={styles.currencyTriggerLabel}>{i18n.language.startsWith('en') ? sel.en : sel.el} ({sel.code})</Text>
                      <Ionicons name={showCurrencyDropdown ? 'chevron-up' : 'chevron-down'} size={14} color={colors.textMuted} />
                    </>
                  ); })()}
                </Pressable>
                {showCurrencyDropdown && (
                  <View style={styles.currencyDropdown}>
                    {CURRENCIES.map((c) => {
                      const sel = currency === c.code;
                      return (
                        <Pressable
                          key={c.code}
                          style={[styles.currencyOption, sel && styles.currencyOptionSelected]}
                          onPress={() => { setCurrency(c.code); setShowCurrencyDropdown(false); }}
                          accessibilityRole="button"
                        >
                          <Text style={[styles.currencyOptionSymbol, sel && styles.currencyOptionSymbolSelected]}>{c.symbol}</Text>
                          <Text style={[styles.currencyOptionLabel, sel && styles.currencyOptionLabelSelected]}>
                            {i18n.language.startsWith('en') ? c.en : c.el} <Text style={styles.currencyOptionCode}>{c.code}</Text>
                          </Text>
                          {sel && <Ionicons name="checkmark" size={14} color={colors.primary} style={{ marginLeft: 'auto' }} />}
                        </Pressable>
                      );
                    })}
                  </View>
                )}
              </View>

              <Text style={styles.fieldLabel}>{t('offersScreens.vatLabel')}</Text>
              <View style={styles.typeCards}>
                <Pressable
                  style={[styles.typeCard, vatMode === 'without' && styles.typeCardSelected]}
                  onPress={() => setVatMode('without')}
                  accessibilityRole="button"
                >
                  <Text style={[styles.typeCardLabel, vatMode === 'without' && styles.typeCardLabelSelected]}>{t('offersScreens.vatWithout')}</Text>
                </Pressable>
                <Pressable
                  style={[styles.typeCard, vatMode === 'with' && styles.typeCardSelected]}
                  onPress={() => setVatMode('with')}
                  accessibilityRole="button"
                >
                  <Text style={[styles.typeCardLabel, vatMode === 'with' && styles.typeCardLabelSelected]}>{t('offersScreens.vatWith')}</Text>
                </Pressable>
              </View>

              {vatMode === 'without' && (
                <View style={styles.field}>
                  <Input
                    label={t('offersScreens.vatRateLabel')}
                    value={vatRate}
                    onChangeText={setVatRate}
                    keyboardType="decimal-pad"
                    placeholder={t('offersScreens.vatRatePlaceholder')}
                  />
                </View>
              )}

              {!!cost.trim() && (
                <View style={styles.totalsCard}>
                  <View style={[styles.totalsRow, vatMode === 'with' && styles.totalsRowFinal]}>
                    <Text style={vatMode === 'with' ? styles.totalsLabelFinal : styles.totalsLabel}>
                      {vatMode === 'without' ? t('offersScreens.priceWithoutVat') : t('offersScreens.finalPriceWithVat')}
                    </Text>
                    <Text style={vatMode === 'with' ? styles.totalsValueFinal : styles.totalsValue}>
                      {serviceCostNum.toFixed(2)}€
                    </Text>
                  </View>
                  {vatMode === 'without' && (
                    <>
                      <View style={styles.totalsRow}>
                        <Text style={styles.totalsLabel}>{t('offersScreens.vatAmountLabel', { rate: vatRateNum })}</Text>
                        <Text style={styles.totalsValue}>+{serviceVatAmount.toFixed(2)}€</Text>
                      </View>
                      <View style={[styles.totalsRow, styles.totalsRowFinal]}>
                        <Text style={styles.totalsLabelFinal}>{t('offersScreens.finalPriceWithVat')}</Text>
                        <Text style={styles.totalsValueFinal}>{serviceTotalWithVat.toFixed(2)}€</Text>
                      </View>
                    </>
                  )}
                </View>
              )}

              {/* Παράδοση (μόνο για non-fixed_term) ή Ημερομηνίες (fixed_term) */}
              {offerType !== 'fixed_term' ? (
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>{t('offers.field_deliveryTime')}</Text>
                  <View style={[styles.inputWrap, !!errors.delivery_days && styles.inputError]}>
                    <TextInput
                      style={styles.input}
                      value={deliveryDays}
                      onChangeText={setDeliveryDays}
                      placeholder={t('offersScreens.deliveryDaysPlaceholder')}
                      placeholderTextColor={colors.textMuted}
                      keyboardType="number-pad"
                    />
                    <Text style={styles.inputSuffix}>{t('offersScreens.daysSuffix')}</Text>
                  </View>
                  {!!errors.delivery_days && <Text style={styles.errorText}>{errors.delivery_days}</Text>}
                </View>
              ) : (
                <View style={styles.field}>
                  <Text style={styles.fieldLabel}>{t('offersScreens.startDateLabel')}</Text>
                  <DatePickerField value={startDate} onChange={setStartDate} minimumDate={new Date()} />
                  <Text style={[styles.fieldLabel, { marginTop: 4 }]}>{t('offersScreens.endDateLabel')}</Text>
                  <DatePickerField value={endDate} onChange={setEndDate} minimumDate={startDate ?? new Date()} />
                  {!!errors.delivery_days && <Text style={styles.errorText}>{errors.delivery_days}</Text>}
                  {startDate && endDate && (
                    <Text style={styles.fieldHint}>
                      {t('offersScreens.durationHint', { days: Math.round((endDate.getTime() - startDate.getTime()) / 86400000) })}
                    </Text>
                  )}
                </View>
              )}

              <View style={styles.field}>
                <Input
                  label={t('offers.field_description')}
                  value={description}
                  onChangeText={setDescription}
                  placeholder={t('offersScreens.serviceDescriptionPlaceholder')}
                  multiline
                  numberOfLines={5}
                  style={styles.multiline}
                />
                {!!errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
              </View>

              <Button title={t('common.preview')} onPress={() => { if (validateStep2()) setWizardStep(3); }} />
            </>
          )}

          {/* ══ Βήμα 2 — Εμπόρευμα ══ */}
          {wizardStep === 2 && offerKind === 'goods' && (
            <>
              {lineItems.map((item, i) => (
                <View key={i} style={styles.productCard}>
                  <View style={styles.productCardHeader}>
                    <Text style={styles.productCardTitle}>{t('offersScreens.productTitle', { index: i + 1 })}</Text>
                    {lineItems.length > 1 && (
                      <Pressable onPress={() => removeLineItem(i)} hitSlop={8}>
                        <Ionicons name="close-circle" size={20} color={colors.textMuted} />
                      </Pressable>
                    )}
                  </View>
                  <Input
                    label={t('offersScreens.productDescriptionLabel')}
                    value={item.description}
                    onChangeText={(v) => updateLineItem(i, { description: v })}
                    placeholder={t('offersScreens.productDescriptionPlaceholder')}
                  />
                  <View style={styles.productRow}>
                    <View style={styles.productField}>
                      <Text style={styles.productFieldLabel} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.75}>{t('offersScreens.productQuantityLabel')}</Text>
                      <TextInput
                        style={styles.productInput}
                        value={item.quantity === 0 ? '' : String(item.quantity)}
                        onChangeText={(v) => updateLineItem(i, { quantity: Number(v.replace(',', '.')) || 0 })}
                        keyboardType="number-pad"
                        placeholder="0"
                        placeholderTextColor={colors.textMuted}
                      />
                    </View>
                    <View style={styles.productField}>
                      <Text style={styles.productFieldLabel} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.75}>{t('offersScreens.productUnitPriceLabel')}</Text>
                      <TextInput
                        style={styles.productInput}
                        value={item.unitPrice === 0 ? '' : formatAmountForDisplay(item.unitPrice)}
                        onChangeText={(v) => updateLineItem(i, { unitPrice: parseGroupedAmount(v) ?? 0 })}
                        keyboardType="decimal-pad"
                        placeholder="0"
                        placeholderTextColor={colors.textMuted}
                      />
                    </View>
                    <View style={styles.productField}>
                      <Text style={styles.productFieldLabel} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.75}>{t('offersScreens.productDiscountLabel')}</Text>
                      <View style={styles.discountInputRow}>
                        <TextInput
                          style={[styles.productInput, styles.discountInput]}
                          value={!item.discount ? '' : String(item.discount)}
                          onChangeText={(v) => updateLineItem(i, { discount: Number(v.replace(',', '.')) || 0 })}
                          keyboardType="decimal-pad"
                          placeholder="0"
                          placeholderTextColor={colors.textMuted}
                        />
                        <View style={styles.discountUnitToggle}>
                          {(['amount', 'percent'] as const).map((dt) => {
                            const sel = (item.discountType ?? 'amount') === dt;
                            return (
                              <Pressable
                                key={dt}
                                onPress={() => updateLineItem(i, { discountType: dt })}
                                style={[styles.discountUnitChip, sel && styles.discountUnitChipSelected]}
                                accessibilityRole="button"
                              >
                                <Text style={[styles.discountUnitChipText, sel && styles.discountUnitChipTextSelected]}>
                                  {dt === 'amount' ? '€' : '%'}
                                </Text>
                              </Pressable>
                            );
                          })}
                        </View>
                      </View>
                    </View>
                  </View>
                </View>
              ))}

              <Pressable style={styles.addProductBtn} onPress={addLineItem} accessibilityRole="button">
                <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
                <Text style={styles.addProductBtnText}>{t('offersScreens.addProduct')}</Text>
              </Pressable>

              <Text style={styles.fieldLabel}>{t('offersScreens.vatLabel')}</Text>
              <View style={styles.typeCards}>
                <Pressable
                  style={[styles.typeCard, vatMode === 'without' && styles.typeCardSelected]}
                  onPress={() => setVatMode('without')}
                  accessibilityRole="button"
                >
                  <Text style={[styles.typeCardLabel, vatMode === 'without' && styles.typeCardLabelSelected]}>{t('offersScreens.vatWithout')}</Text>
                </Pressable>
                <Pressable
                  style={[styles.typeCard, vatMode === 'with' && styles.typeCardSelected]}
                  onPress={() => setVatMode('with')}
                  accessibilityRole="button"
                >
                  <Text style={[styles.typeCardLabel, vatMode === 'with' && styles.typeCardLabelSelected]}>{t('offersScreens.vatWith')}</Text>
                </Pressable>
              </View>

              {vatMode === 'without' && (
                <View style={styles.field}>
                  <Input
                    label={t('offersScreens.vatRateLabel')}
                    value={vatRate}
                    onChangeText={setVatRate}
                    keyboardType="decimal-pad"
                    placeholder={t('offersScreens.vatRatePlaceholder')}
                  />
                </View>
              )}

              <View style={styles.totalsCard}>
                {hasAnyDiscount && (
                  <>
                    <View style={styles.totalsRow}>
                      <Text style={styles.totalsLabel}>{t('offersScreens.subtotalLabel')}</Text>
                      <Text style={styles.totalsValue}>{lineItemsTotals.subtotal.toFixed(2)}€</Text>
                    </View>
                    <View style={styles.totalsRow}>
                      <Text style={styles.totalsLabel}>{t('offersScreens.discountLabel')}</Text>
                      <Text style={[styles.totalsValue, { color: colors.danger }]}>-{lineItemsTotals.discountTotal.toFixed(2)}€</Text>
                    </View>
                  </>
                )}
                <View style={[styles.totalsRow, vatMode === 'with' && styles.totalsRowFinal]}>
                  <Text style={vatMode === 'with' ? styles.totalsLabelFinal : styles.totalsLabel}>
                    {vatMode === 'without' ? t('offersScreens.totalWithoutVat') : t('offersScreens.finalTotalWithVat')}
                  </Text>
                  <Text style={vatMode === 'with' ? styles.totalsValueFinal : styles.totalsValue}>
                    {lineItemsTotals.finalTotal.toFixed(2)}€
                  </Text>
                </View>
                {vatMode === 'without' && (
                  <>
                    <View style={styles.totalsRow}>
                      <Text style={styles.totalsLabel}>{t('offersScreens.vatAmountLabel', { rate: vatRateNum })}</Text>
                      <Text style={styles.totalsValue}>+{vatAmount.toFixed(2)}€</Text>
                    </View>
                    <View style={[styles.totalsRow, styles.totalsRowFinal]}>
                      <Text style={styles.totalsLabelFinal}>{t('offersScreens.finalTotalWithVat')}</Text>
                      <Text style={styles.totalsValueFinal}>{totalWithVat.toFixed(2)}€</Text>
                    </View>
                  </>
                )}
              </View>

              {!!errors.cost && <Text style={styles.errorText}>{errors.cost}</Text>}

              <View style={styles.field}>
                <Input
                  label={t('offers.field_description')}
                  value={description}
                  onChangeText={setDescription}
                  placeholder={t('offersScreens.goodsDescriptionPlaceholder')}
                  multiline
                  numberOfLines={4}
                  style={styles.multiline}
                />
                {!!errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
              </View>

              <Button title={t('common.preview')} onPress={() => { if (validateStep2()) setWizardStep(3); }} />
            </>
          )}

          {/* ══ Βήμα 3 — Επισκόπηση ══ */}
          {wizardStep === 3 && (
            <>
              <View style={styles.reviewCard}>
                <Text style={styles.reviewTitle}>{t('offers.review_title')}</Text>
                <View style={styles.reviewRow}>
                  <Text style={styles.reviewLabel}>{t('offers.review_titleLabel')}</Text>
                  <Text style={styles.reviewValue}>{title.trim()}</Text>
                </View>
                <View style={styles.reviewRow}>
                  <Text style={styles.reviewLabel}>{t('offers.review_recipientLabel')}</Text>
                  <Text style={styles.reviewValue}>{recipientDisplay}</Text>
                </View>
                {offerKind === 'goods' ? (
                  <>
                    <View style={styles.reviewDivider} />
                    {lineItems.map((it, i) => (
                      <View key={i} style={styles.reviewRow}>
                        <Text style={styles.reviewLabel} numberOfLines={1}>{it.description || t('offersScreens.productTitle', { index: i + 1 })} ×{it.quantity}</Text>
                        <Text style={styles.reviewValue}>{lineItemTotal(it).toFixed(2)}€</Text>
                      </View>
                    ))}
                    {hasAnyDiscount && (
                      <View style={styles.reviewRow}>
                        <Text style={styles.reviewLabel}>{t('offersScreens.discountLabel')}</Text>
                        <Text style={styles.reviewValue}>-{lineItemsTotals.discountTotal.toFixed(2)}€</Text>
                      </View>
                    )}
                    <View style={styles.reviewRow}>
                      <Text style={styles.reviewLabel}>{vatMode === 'without' ? t('offersScreens.totalWithoutVat') : t('offersScreens.finalTotalWithVat')}</Text>
                      <Text style={styles.reviewValue}>{lineItemsTotals.finalTotal.toFixed(2)}€</Text>
                    </View>
                    {vatMode === 'without' && (
                      <>
                        <View style={styles.reviewRow}>
                          <Text style={styles.reviewLabel}>{t('offersScreens.vatAmountLabel', { rate: vatRateNum })}</Text>
                          <Text style={styles.reviewValue}>+{vatAmount.toFixed(2)}€</Text>
                        </View>
                        <View style={styles.reviewRow}>
                          <Text style={styles.reviewLabel}>{t('offersScreens.finalTotalWithVat')}</Text>
                          <Text style={styles.reviewValue}>{totalWithVat.toFixed(2)}€</Text>
                        </View>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <View style={styles.reviewRow}>
                      <Text style={styles.reviewLabel}>
                        {vatMode === 'without' ? `${t('offers.review_costLabel')} (${t('offersScreens.vatWithout')})` : t('offers.review_costLabel')}
                      </Text>
                      <Text style={styles.reviewValue}>{cost.trim() ? `${cost} €` : '—'}</Text>
                    </View>
                    {vatMode === 'without' && !!cost.trim() && (
                      <>
                        <View style={styles.reviewRow}>
                          <Text style={styles.reviewLabel}>{t('offersScreens.vatAmountLabel', { rate: vatRateNum })}</Text>
                          <Text style={styles.reviewValue}>+{serviceVatAmount.toFixed(2)}€</Text>
                        </View>
                        <View style={styles.reviewRow}>
                          <Text style={styles.reviewLabel}>{t('offersScreens.finalPriceWithVat')}</Text>
                          <Text style={styles.reviewValue}>{serviceTotalWithVat.toFixed(2)}€</Text>
                        </View>
                      </>
                    )}
                    {vatMode === 'with' && !!cost.trim() && (
                      <View style={styles.reviewRow}>
                        <Text style={styles.reviewLabel}>{t('offersScreens.finalPriceLabel')}</Text>
                        <Text style={styles.reviewValue}>{t('offersScreens.finalPriceWithVatTag', { cost })}</Text>
                      </View>
                    )}
                    <View style={styles.reviewRow}>
                      <Text style={styles.reviewLabel}>{t('offersScreens.reviewTypeLabel')}</Text>
                      <Text style={styles.reviewValue}>
                        {offerType === 'one_off' ? t('offers.type_one_off') : offerType === 'ongoing' ? t('offers.type_ongoing') : t('offers.type_fixed_term')}
                      </Text>
                    </View>
                    {offerType !== 'fixed_term' ? (
                      <View style={styles.reviewRow}>
                        <Text style={styles.reviewLabel}>{t('offers.review_deliveryLabel')}</Text>
                        <Text style={styles.reviewValue}>{deliveryDays.trim() ? `${deliveryDays} ${t('offers.days_suffix')}` : '—'}</Text>
                      </View>
                    ) : startDate && endDate ? (
                      <View style={styles.reviewRow}>
                        <Text style={styles.reviewLabel}>{t('offersScreens.reviewPeriodLabel')}</Text>
                        <Text style={styles.reviewValue}>
                          {startDate.toLocaleDateString('el-GR')} – {endDate.toLocaleDateString('el-GR')}
                        </Text>
                      </View>
                    ) : null}
                  </>
                )}
                {!!description.trim() && (
                  <View style={styles.reviewRowFull}>
                    <Text style={styles.reviewLabel}>{t('offers.review_descriptionLabel')}</Text>
                    <Text style={styles.reviewValueMuted}>{description.trim()}</Text>
                  </View>
                )}
              </View>

              <ProfessionalTermsOption profile={profile} value={includeTerms} onChange={setIncludeTerms} />

              <View style={styles.infoBox}>
                <Ionicons name="information-circle-outline" size={16} color={GOLD} />
                <Text style={styles.infoText}>{t('offers.inkInfo')}</Text>
              </View>

              {!!formError && <Text style={styles.errorText}>{formError}</Text>}
              <Button title={t('offers.action_send')} icon="send-outline" onPress={onSubmit} loading={loading} />
            </>
          )}
        </View>
      </ScrollView>

      {/* ── Tutorial bottom sheet ── */}
      {coachStep !== null && (
        <Modal visible transparent animationType="slide" onRequestClose={skipCoach}>
          <View style={styles.coachOverlay} pointerEvents="box-none">
            <Pressable style={[styles.coachSheet, { paddingBottom: insets.bottom + spacing.lg }]} onPress={() => {}}>
              <View style={styles.coachHandle} />

              <View style={styles.coachDots}>
                {COACH_STEPS.map((_, i) => (
                  <View key={i} style={[styles.coachDot, i === coachStep && styles.coachDotActive]} />
                ))}
              </View>

              <View style={styles.coachIconWrap}>
                <Ionicons name={COACH_STEPS[coachStep].icon as never} size={32} color={colors.primary} />
              </View>
              <Text style={styles.coachTitle}>{COACH_STEPS[coachStep].title}</Text>
              <Text style={styles.coachText}>{COACH_STEPS[coachStep].text}</Text>

              <View style={styles.coachActions}>
                <Pressable onPress={skipCoach} hitSlop={8} style={styles.coachSkipBtn}>
                  <Text style={styles.coachSkip}>{t('offersScreens.coachSkip')}</Text>
                </Pressable>
                <Pressable style={styles.coachNext} onPress={nextCoach}>
                  <Text style={styles.coachNextText}>
                    {coachStep < COACH_STEPS.length - 1 ? t('offersScreens.coachNext') : t('offersScreens.coachStart')}
                  </Text>
                </Pressable>
              </View>
            </Pressable>
          </View>
        </Modal>
      )}
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    flex1: { flex: 1 },
    content: {
      // Σε μεγάλες οθόνες η φόρμα μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.form,
      alignSelf: 'center',
      flexGrow: 1,
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    topBar: { flexDirection: 'row' },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600' },
    header: { gap: spacing.sm },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    title: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700' },
    subtitle: { color: colors.textMuted, fontSize: fontSize.md, lineHeight: 22 },
    form: { gap: spacing.md },
    entryCard: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.md,
      backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border,
      padding: spacing.lg,
    },
    entryCardTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    entryCardSub: { color: colors.textMuted, fontSize: fontSize.sm, marginTop: 2 },
    manageTemplatesLink: {
      flexDirection: 'row', alignItems: 'center', gap: 6,
      alignSelf: 'center', paddingVertical: spacing.sm,
    },
    manageTemplatesLinkText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: spacing.lg },
    modalCard: { backgroundColor: colors.surface, borderRadius: radius.lg, padding: spacing.lg, gap: spacing.md },
    modalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    modalActions: { flexDirection: 'row', gap: spacing.sm },
    templateRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
    templateRowText: { color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    templateRowSub: { color: colors.textMuted, fontSize: fontSize.xs, marginTop: 1 },
    field: { gap: spacing.xs },
    fieldLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500' },
    fieldHint: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 18 },
    row: { flexDirection: 'row', gap: spacing.md },
    inputWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      height: 52,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingLeft: spacing.md,
      paddingRight: spacing.sm,
    },
    inputError: { borderColor: colors.danger },
    input: { flex: 1, color: colors.text, fontSize: fontSize.md },
    inputSuffix: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    currencyTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      alignSelf: 'flex-start',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingVertical: 7,
      paddingHorizontal: spacing.sm,
      backgroundColor: colors.surfaceAlt,
    },
    currencyTriggerSymbol: { color: colors.text, fontSize: fontSize.md, fontWeight: '800', minWidth: 14, textAlign: 'center' },
    currencyTriggerLabel: { color: colors.textMuted, fontSize: fontSize.sm },
    currencyDropdown: {
      marginTop: 4,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      backgroundColor: colors.surface,
      overflow: 'hidden',
    },
    currencyOption: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: 10,
      paddingHorizontal: spacing.md,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    currencyOptionSelected: { backgroundColor: `${colors.primary}12` },
    currencyOptionSymbol: { color: colors.textMuted, fontSize: fontSize.md, fontWeight: '700', width: 20, textAlign: 'center' },
    currencyOptionSymbolSelected: { color: colors.primary },
    currencyOptionLabel: { color: colors.text, fontSize: fontSize.sm, flex: 1 },
    currencyOptionLabelSelected: { color: colors.primary, fontWeight: '700' },
    currencyOptionCode: { color: colors.textMuted, fontSize: fontSize.xs },
    multiline: { height: 170, paddingTop: spacing.md, textAlignVertical: 'top' },
    searchFieldWrap: { gap: spacing.xs },
    selectedChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      height: 52,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.md,
      paddingHorizontal: spacing.md,
    },
    selectedChipText: { flex: 1, color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    dropdown: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      overflow: 'hidden',
    },
    dropdownItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    dropdownMeta: { flex: 1 },
    dropdownName: { color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    dropdownEmail: { color: colors.textMuted, fontSize: fontSize.sm },
    dropdownSub: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    typeCards: { flexDirection: 'row', gap: spacing.xs },
    typeCard: {
      flex: 1,
      alignItems: 'center',
      gap: 4,
      padding: spacing.sm,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    typeCardSelected: { borderColor: colors.primary, backgroundColor: `${colors.primary}14` },
    typeCardLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '700' },
    typeCardLabelSelected: { color: colors.primary },
    typeCardSub: { color: colors.textMuted, fontSize: 10, textAlign: 'center' },
    infoBox: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      backgroundColor: 'rgba(201,168,67,0.08)',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: 'rgba(201,168,67,0.20)',
      padding: spacing.md,
    },
    infoText: { flex: 1, color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20 },
    errorText: { color: colors.danger, fontSize: fontSize.sm },
    // Review step
    reviewCard: { backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border, padding: spacing.lg, gap: spacing.md },
    reviewTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', marginBottom: spacing.xs },
    reviewRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: spacing.sm },
    reviewRowFull: { gap: spacing.xs },
    reviewLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500', minWidth: 90 },
    reviewValue: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600', flex: 1, textAlign: 'right' },
    reviewValueMuted: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20 },
    reviewDivider: { height: 1, backgroundColor: colors.border, marginVertical: spacing.xs },
    // Goods (εμπόρευμα) step
    productCard: {
      backgroundColor: colors.surface, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border,
      padding: spacing.md, gap: spacing.sm,
    },
    productCardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    productCardTitle: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700' },
    productRow: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-end' },
    productField: { flex: 1, gap: 4 },
    productFieldLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500' },
    productInput: {
      height: 44, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border,
      backgroundColor: colors.surfaceAlt, color: colors.text, textAlign: 'center', fontSize: fontSize.sm, paddingHorizontal: 4,
    },
    discountInputRow: { flexDirection: 'row', gap: 4 },
    discountInput: { flex: 1 },
    discountUnitToggle: { flexDirection: 'row', gap: 2 },
    discountUnitChip: {
      minWidth: 30, alignItems: 'center', justifyContent: 'center', borderRadius: radius.sm,
      borderWidth: 1, borderColor: colors.border, paddingHorizontal: 6,
    },
    discountUnitChipSelected: { backgroundColor: colors.primary, borderColor: colors.primary },
    discountUnitChipText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '700' },
    discountUnitChipTextSelected: { color: colors.textOnPrimary },
    addProductBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, alignSelf: 'flex-start', padding: spacing.xs },
    addProductBtnText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    totalsCard: {
      backgroundColor: colors.surfaceAlt, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border,
      padding: spacing.md, gap: 6,
    },
    totalsRow: { flexDirection: 'row', justifyContent: 'space-between' },
    totalsRowFinal: { borderTopWidth: 1, borderTopColor: colors.border, paddingTop: 6, marginTop: 2 },
    totalsLabel: { color: colors.textMuted, fontSize: fontSize.sm },
    totalsValue: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
    totalsLabelFinal: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    totalsValueFinal: { color: colors.primary, fontSize: fontSize.md, fontWeight: '700' },
    // Success screen
    successWrap: {
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    successIcon: { marginBottom: spacing.md },
    successTitle: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700', textAlign: 'center' },
    successText: { color: colors.textMuted, fontSize: fontSize.md, textAlign: 'center', lineHeight: 22 },
    emailSentBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: 'rgba(34,197,94,0.10)',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: 'rgba(34,197,94,0.25)',
      padding: spacing.md,
      width: '100%',
    },
    emailSentText: { color: colors.success, fontSize: fontSize.sm, fontWeight: '600', flex: 1 },
    deliveredBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: 'rgba(34,197,94,0.10)',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: 'rgba(34,197,94,0.25)',
      padding: spacing.md,
      width: '100%',
    },
    deliveredBannerText: { color: colors.text, fontSize: fontSize.sm, lineHeight: 20, flex: 1 },
    // Tutorial bottom sheet
    coachOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'flex-end',
    },
    coachSheet: {
      backgroundColor: colors.surface,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      paddingHorizontal: spacing.xl,
      paddingTop: spacing.md,
      gap: spacing.md,
      borderTopWidth: 1,
      borderColor: colors.border,
    },
    coachHandle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: colors.border,
      alignSelf: 'center',
      marginBottom: spacing.sm,
    },
    coachDots: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 6,
    },
    coachDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: colors.border,
    },
    coachDotActive: {
      backgroundColor: colors.primary,
      width: 20,
    },
    coachIconWrap: {
      alignSelf: 'center',
      width: 60,
      height: 60,
      borderRadius: 30,
      backgroundColor: 'rgba(99,102,241,0.10)',
      alignItems: 'center',
      justifyContent: 'center',
    },
    coachTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
      textAlign: 'center',
    },
    coachText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 22,
      textAlign: 'center',
    },
    coachActions: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: spacing.xs,
    },
    coachSkipBtn: { padding: spacing.sm },
    coachSkip: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    coachNext: {
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.xl,
    },
    coachNextText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
  });
}
